import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@mantine/core';
import {
  IconBell,
  IconCar,
  IconFiles,
  IconFirstAidKit,
  IconLogout,
  IconPalette,
  IconPhoto,
  IconShieldCheck,
  IconSignature,
  IconTrash,
  IconUser,
} from '@tabler/icons-react';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { useDriverProfile } from '@/lib/me';
import { useMyPhoto } from '@/lib/photo';
import { photoRules, useCopy } from '@/lib/settings';
import { themeChoiceLabel, useThemeChoice } from '@/theme/colorScheme';
import { ProfilePhotoCard } from '@/components/rides/ProfilePhotoCard';
import { DocumentsCard, useDocumentsSummary } from '@/components/rides/DocumentsCard';
import { PushOptInCard, usePushOptIn } from '@/components/rides/PushOptInCard';
import { Pill } from '@/components/rides/Pill';
import { PlateChip } from '@/components/rides/PlateChip';
import { AboutCarpoolCard, ThemeChoice } from '@/components/ui';
import { ProfileHeader } from '@/components/member/ProfileHeader';
import { SettingsGroup, SettingsRow } from '@/components/member/Settings';
import {
  CarBody,
  ChecksBody,
  ConsentsBody,
  DeleteBody,
  DetailsBody,
  EmergencyBody,
  useChecksSummary,
  useDetailsSummary,
  useEmergencySummary,
} from '@/components/member/AccountSections';
import { carWords } from '@/components/member/trips';
import { useRidesWizard } from '@/store/ridesWizard';
import '@/styles/member.css';

/**
 * Account, laid out like a phone's settings screen: a profile header, then grouped rows that open
 * in place (#details, #photo, #emergency, #car, #checks, #documents, #appearance, #notifications,
 * #consents, #delete), sign out, and "About Carpool" (privacy notice, terms, help, version, Acts
 * Church logo and Powered by CCMS). Rows the member has nothing in are left out.
 */
export default function AccountPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useEnrichedAuth();
  const { t, settings } = useCopy();
  const driver = useDriverProfile();
  const reset = useRidesWizard((s) => s.reset);
  const photos = photoRules(settings);
  const photo = useMyPhoto();
  const { choice } = useThemeChoice();
  const details = useDetailsSummary();
  const checks = useChecksSummary();
  const docs = useDocumentsSummary(!!driver.data);
  const emergency = useEmergencySummary();
  const push = usePushOptIn();

  // Deep links from Home (/account#photo, /account#documents) and the menus open and show the row.
  useEffect(() => {
    const id = location.hash.replace(/^#/, '');
    if (!id) return;
    const timer = window.setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 250);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  return (
    <div className="rides-main">
      <h1 className="sr-only">Account</h1>
      <ProfileHeader />

      <SettingsGroup title="Your profile">
        <SettingsRow id="details" icon={<IconUser size={21} />} title="Personal details" summary={details}>
          <DetailsBody />
        </SettingsRow>
        {photos.enabled && (
          <SettingsRow id="photo" icon={<IconPhoto size={21} />} tone="rose" title="Profile photo" summary={photo.hasPhoto ? 'Added' : 'Not added yet'} wide>
            <ProfilePhotoCard
              plain
              removeWarning={photos.driverRequired && driver.data ? "You'll need a photo again before you can offer a lift." : undefined}
            />
          </SettingsRow>
        )}
        {emergency.visible && (
          <SettingsRow
            id="emergency"
            icon={<IconFirstAidKit size={21} />}
            tone="red"
            title="Emergency contact"
            summary={emergency.summary}
            trailing={emergency.needed ? <Pill kind="warn">Needed</Pill> : undefined}
            wide
          >
            <EmergencyBody />
          </SettingsRow>
        )}
      </SettingsGroup>

      {driver.data && (
        <SettingsGroup title="Driving">
          <SettingsRow
            id="car"
            icon={<IconCar size={21} />}
            tone="violet"
            title="Car and licence"
            summary={carWords(driver.data.vehicle)}
            trailing={driver.data.isSuspended ? <Pill kind="bad">On hold</Pill> : <PlateChip plate={driver.data.vehicle.plate} />}
          >
            <CarBody />
          </SettingsRow>
        </SettingsGroup>
      )}

      <SettingsGroup title="Checks and documents">
        <SettingsRow
          id="checks"
          icon={<IconShieldCheck size={21} />}
          tone="green"
          title="ID checks"
          summary={driver.data ? 'Your ID and licence' : 'Your ID'}
          trailing={checks ? <Pill kind={checks.tone}>{checks.label}</Pill> : undefined}
        >
          <ChecksBody />
        </SettingsRow>
        {docs.visible && (
          <SettingsRow
            id="documents"
            icon={<IconFiles size={21} />}
            tone="amber"
            title="Documents"
            summary="Seen only by the Carpool team"
            trailing={docs.label ? <Pill kind={docs.tone}>{docs.label}</Pill> : undefined}
            wide
          >
            <DocumentsCard plain context="all" isDriver={!!driver.data} />
          </SettingsRow>
        )}
      </SettingsGroup>

      <SettingsGroup title="Settings">
        <SettingsRow id="appearance" icon={<IconPalette size={21} />} tone="violet" title="Appearance" summary={t('account.appearance_help')} alwaysOpen wide>
          <ThemeChoice label={null} />
          <span className="sr-only">Current choice: {themeChoiceLabel(choice)}</span>
        </SettingsRow>
        {push.available && (
          <SettingsRow
            id="notifications"
            icon={<IconBell size={21} />}
            tone="orange"
            title="Ride-day alerts"
            summary="When your driver arrives or a ride changes"
            trailing={<span>{push.granted ? 'On' : push.denied ? 'Blocked' : 'Off'}</span>}
          >
            <PushOptInCard variant="plain" />
          </SettingsRow>
        )}
      </SettingsGroup>

      <SettingsGroup title="Privacy">
        <SettingsRow id="consents" icon={<IconSignature size={21} />} title="What you agreed to" summary="Your consents">
          <ConsentsBody />
        </SettingsRow>
        <SettingsRow id="delete" icon={<IconTrash size={21} />} tone="red" title="Delete my data" summary="Ask us to remove your information">
          <DeleteBody />
        </SettingsRow>
      </SettingsGroup>

      <div className="rm-signout">
        <Button
          variant="default"
          fullWidth
          size="lg"
          leftSection={<IconLogout size={20} />}
          onClick={async () => { reset(); await logout(); navigate('/'); }}
        >
          Sign out
        </Button>
      </div>

      <div style={{ marginTop: 22 }}>
        <AboutCarpoolCard deleteHref={null} />
      </div>
    </div>
  );
}
